import React, { useState, useEffect } from 'react';
import { Grid, GridItem, VStack, Text, Box, HStack, Button, Badge, Flex, Divider } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import Card from './Card';
import { useGame } from '../../context/GameContext';
import FortuneWheel from './FortuneWheel';
import SymbolMultiplierDisplay from './SymbolMultiplierDisplay';

// Define fallback values for symbols and multipliers in case they're not available from context
const fallbackSymbols = [
  { symbol: 'A', weight: 20, payout: 2, type: 'Common' },
  { symbol: 'B', weight: 18, payout: 2, type: 'Common' },
  { symbol: 'C', weight: 15, payout: 3, type: 'Uncommon' },
  { symbol: 'D', weight: 12, payout: 4, type: 'Uncommon' },
  { symbol: 'E', weight: 10, payout: 6, type: 'Rare' },
  { symbol: 'F', weight: 8, payout: 8, type: 'Rare' },
  { symbol: 'G', weight: 6, payout: 12, type: 'Epic' },
  { symbol: 'H', weight: 4, payout: 20, type: 'Epic' },
  { symbol: 'I', weight: 3, payout: 30, type: 'Legendary' },
  { symbol: 'J', weight: 2, payout: 50, type: 'Legendary' },
  { symbol: 'W', weight: 2, payout: 0, type: 'Wildcard' },
  { symbol: 'Key', weight: 0, payout: 0, type: 'Bonus' },
  { symbol: 'Wheel', weight: 0, payout: 0, type: 'Bonus' },
  { symbol: 'Ticket', weight: 0, payout: 0, type: 'Bonus' }
];

const fallbackMultipliers = {
  3: 1,
  4: 1.5,
  5: 2,
  6: 3,
  7: 5,
  8: 10,
  9: 10,
  10: 10,
  11: 10,
  12: 10
};

const GamePanel = () => {
    const { gameState, flipCard, getGameSymbols, getMatchMultipliers, setBonusFeatures, playGame } = useGame();
    
    // Use the symbols and multipliers from context if available, otherwise use fallbacks
    const gameSymbols = getGameSymbols ? getGameSymbols() : fallbackSymbols;
    const matchMultipliers = getMatchMultipliers ? getMatchMultipliers() : fallbackMultipliers;
    
    // Safely destructure gameState with default values
    const { 
        cards = [], 
        revealedCards = Array(12).fill(false), 
        winAmount = 0, 
        matches = [],
        bonusFeatures = { 
            activeBonus: null, 
            bonusResult: null, 
            bonusMultiplier: 0,
            wheelBonus: false,
            chestBonus: false,
            lotteryBonus: false
        },
        isPlaying = false,
        betAmount = 1
    } = gameState || {};
    
    const [showSymbolGuide, setShowSymbolGuide] = useState(false);
    const [tokensWon, setTokensWon] = useState(0);

    // Update tokens won when winAmount changes
    useEffect(() => {
        if (winAmount > 0) {
            setTokensWon(winAmount);
        }
    }, [winAmount]);

    // Handle bonus feature animations and displays
    useEffect(() => {
        if (bonusFeatures.activeBonus) {
            // Could add sound effects or additional animations here
            const timer = setTimeout(() => {
                // Reset bonus display after a few seconds
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [bonusFeatures.activeBonus]);

    const getWinMultiplier = () => {
        if (matches?.length > 0 && matches[0] && matches[0].positions) {
            const matchCount = matches[0].positions.length;
            // Make sure we're using the correct multiplier from the matchMultipliers object
            return matchMultipliers[matchCount] || 1;
        }
        return 1;
    };

    // Get the multiplier for a specific match
    const getMatchMultiplierValue = (match) => {
        if (match && match.positions) {
            const matchCount = match.positions.length;
            return matchMultipliers[matchCount] || 1;
        }
        return 1;
    };

    // Close bonus feature
    const closeBonusFeature = () => {
        setBonusFeatures(prev => ({
            ...prev,
            activeBonus: null
        }));
    };

    // Handle buying a new card
    const handleBuyCard = () => {
        // Reset tokens won display when buying a new card
        setTokensWon(0);
        // Start a new game
        playGame();
    };

    return (
        <VStack spacing={8}>
            {/* Symbol Guide */}
            {showSymbolGuide && (
                <Box 
                    p={4} 
                    bg="whiteAlpha.200" 
                    borderRadius="md" 
                    w="100%" 
                    mb={4}
                    borderColor="purple.400"
                    borderWidth="1px"
                >
                    <Text fontWeight="bold" mb={2}>Symbol Guide:</Text>
                    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                        {gameSymbols.map((symbol) => (
                            <HStack key={symbol.symbol} spacing={2}>
                                <Box 
                                    w="30px" 
                                    h="30px" 
                                    bg="purple.700" 
                                    borderRadius="md" 
                                    display="flex" 
                                    alignItems="center" 
                                    justifyContent="center"
                                    color="white"
                                >
                                    {symbol.symbol}
                                </Box>
                                <Text fontSize="sm">
                                    {symbol.type} ({symbol.payout}x)
                                </Text>
                            </HStack>
                        ))}
                    </Grid>
                    <Text fontSize="sm" mt={2}>
                        Match 3: 1.0x • 4: 1.1x • 5: 1.2x • 6: 1.3x • 7: 1.4x • 8+: 1.5x+
                    </Text>
                </Box>
            )}

            <Grid
                templateColumns="repeat(4, 1fr)"
                templateRows="repeat(3, 1fr)"
                gap={{ base: 4, md: 6 }}
                w="100%"
                maxW={{ base: "100%", md: "1300px" }}
                mx="auto"
                position="relative"
            >
                {Array(12).fill(null).map((_, index) => {
                    const card = cards[index] || {};
                    const isRevealed = revealedCards[index] || false;
                    const isMatched = matches?.some(match => match.positions?.includes(index)) || false;

                    return (
                        <GridItem
                            key={index}
                            position="relative"
                            w="100%"
                            pb="140%" // Keep original height but cards will be wider due to grid changes
                        >
                            <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                            >
                                <Card
                                    id={index}
                                    isFlipped={isRevealed}
                                    onFlip={() => flipCard?.(index)}
                                    disabled={!isPlaying || isRevealed}
                                    symbol={card?.symbol}
                                    isMatched={isMatched}
                                />
                            </Box>
                        </GridItem>
                    );
                })}
            </Grid>

            {/* Symbol Multiplier Display Component */}
            <SymbolMultiplierDisplay />
            
            {/* Bonus Feature Components */}
            {bonusFeatures.activeBonus === 'wheel' && (
                <FortuneWheel 
                    isOpen={bonusFeatures.activeBonus === 'wheel'} 
                    onClose={closeBonusFeature} 
                    betAmount={gameState.betAmount}
                />
            )}
        </VStack>
    );
};

export default GamePanel;
